import type { Permissions } from "../../../../services/lbmpService";

import { getPulseTracker } from "../../../../pulse";
import {
  getAnonymousPermissions,
  getUserPermissions,
} from "../../../../services/lbmpService";

type Asset = { [key: string]: string };
type AssetsMap = {
  [key: string]: Asset;
};

const assetsMap: AssetsMap = {
  glimr: {
    purpose: "sdrn:omnise:processingpurpose:targeted_advertising",
    data: "sdrn:schibsted-media:datacategory:geolocation",
  },
};

interface GeoTrackingCache {
  value: boolean;
  isLoggedIn: boolean;
  userId: string;
  sig: string;
  pathname: string;
  timestamp: number;
}

let geoTrackingCache: GeoTrackingCache | null = null;
let geoTrackingPromise: Promise<boolean> | null = null;

const GEO_TRACKING_CACHE_TTL = 5 * 60 * 1000;

const getGeoTrackingPermission = async (
  loggedIn: boolean,
  user_id: string,
  sig: string,
): Promise<boolean> => {
  const currentPathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const now = Date.now();

  // Invalidate cache if:
  // 1. Different pathname (user navigated)
  // 2. Login state changed (logged in/out)
  // 3. User ID or signature changed (session change)
  // 4. Cache expired (TTL exceeded)
  // 5. No cache exists
  if (
    !geoTrackingCache ||
    geoTrackingCache.pathname !== currentPathname ||
    geoTrackingCache.isLoggedIn !== loggedIn ||
    geoTrackingCache.userId !== user_id ||
    geoTrackingCache.sig !== sig ||
    now - geoTrackingCache.timestamp > GEO_TRACKING_CACHE_TTL
  ) {
    geoTrackingCache = null;
    geoTrackingPromise = null;
  }

  if (geoTrackingCache) {
    return geoTrackingCache.value;
  }

  if (geoTrackingPromise) {
    return geoTrackingPromise;
  }

  // Fetch geo tracking permission
  const fetchPromise = (async (): Promise<boolean> => {
    try {
      const purpose = assetsMap.glimr.purpose;

      let permissions: Permissions = [];

      const browser = window as any;

      if (!browser) {
        return false;
      }

      let hasPermission = false;

      if (loggedIn && sig) {
        const response = await getUserPermissions(
          `sdrn:schibsted.com:user:${user_id}`,
          sig,
        );
        if (!response.data) {
          hasPermission = false;
        } else {
          permissions = response.data?.permissions || [];
          hasPermission = !!permissions.find(
            (permission) => permission.purpose === purpose,
          );
        }
      } else {
        const pulseTracker = getPulseTracker();
        const pulseEnvId = await pulseTracker?.pulseTracker.getEnvironmentId();

        const response = await getAnonymousPermissions(
          `sdrn:schibsted:environment:${pulseEnvId}`,
        );
        if (!response.data) {
          hasPermission = false;
        } else {
          permissions = response.data?.permissions || [];
          hasPermission = !!permissions.find(
            (permission) => permission.purpose === purpose,
          );
        }
      }

      // Cache with metadata (for both logged-in and anonymous users)
      geoTrackingCache = {
        value: hasPermission,
        isLoggedIn: loggedIn,
        userId: user_id,
        sig,
        pathname: currentPathname,
        timestamp: now,
      };
      geoTrackingPromise = null;

      return hasPermission;
    } catch (e) {
      console.error(
        `getGeoTrackingPermission: Failed to get geo tracking permission`,
        e,
      );
      geoTrackingPromise = null;
      return false;
    }
  })();

  geoTrackingPromise = fetchPromise;
  return fetchPromise;
};

const invalidateGeoTrackingCache = () => {
  geoTrackingCache = null;
  geoTrackingPromise = null;
};

export { getGeoTrackingPermission, invalidateGeoTrackingCache };
